import React, { useEffect, useRef, useLayoutEffect } from 'react';
import { GridController } from '../compositor/GridController';

interface SpreadsheetCanvasProps {
    controller: GridController;
    className?: string;
    style?: React.CSSProperties;
    inputTarget?: HTMLElement | null;
    // Parity capture: tag the underlying canvas in the DOM.
    captureTag?: { tabId: string; kind: 'query' | 'sheet' };
}

export const SpreadsheetCanvas: React.FC<SpreadsheetCanvasProps> = ({ controller, className, style, inputTarget, captureTag }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        controller.attach(canvas, inputTarget || undefined);

        return () => {
            // controller.dispose(); // Don't dispose controller here as it might be owned by parent
        };
    }, [controller, inputTarget]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const snapCssPixels = (value: number) => {
            const raw = Math.max(1, Number(value) || 1);
            const dpr = (typeof window !== 'undefined' && Number.isFinite(window.devicePixelRatio) && window.devicePixelRatio > 0)
                ? window.devicePixelRatio
                : 1;
            return Math.max(1, Math.round(raw * dpr) / dpr);
        };

        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const { width, height } = entry.contentRect;
                controller.resize(snapCssPixels(width), snapCssPixels(height));
            }
        });

        observer.observe(container);

        return () => {
            observer.disconnect();
        };
    }, [controller]);

    return (
        <div ref={containerRef} className={className} style={{ ...style, position: 'relative', overflow: 'hidden' }}>
            <canvas
                ref={canvasRef}
                style={{ display: 'block', width: '100%', height: '100%' }}
                data-fg-canvas-kind={captureTag ? 'grid' : undefined}
                data-fg-tab-kind={captureTag?.kind}
                data-fg-tab-id={captureTag?.tabId}
            />
        </div>
    );
};
